import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Clock, LayoutDashboard, CalendarRange, Timer, LogIn, LogOut, Loader2, Plus, X, Trash2, Check, XCircle } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import SelectField from "../../components/os/SelectField";
import DatePicker from "../../components/os/DatePicker";
import { EmployeePicker } from "../../components/os/EmployeePicker";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const LEADERSHIP = ["admin", "ceo", "director"];

function Modal({ title, onClose, children, testid }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        className="card-surface w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{title}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

const hhmm = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

/* ============ PANEL ============ */
function DashboardTab() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/workforce/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const kpis = [
    { label: t("os.workforce.presentToday"), value: d.present_today }, { label: t("os.workforce.hoursWeek"), value: d.hours_week },
    { label: t("os.workforce.shiftsToday"), value: d.shifts_today }, { label: t("os.workforce.pendingTimesheets"), value: d.pending_timesheets },
  ];
  return (
    <div className="space-y-8" data-testid="wf-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <div key={k.label} className="card-surface p-5" data-testid={`wf-dash-kpi-${i}`}>
            <div className="overline text-navy/40">{k.label}</div>
            <div className="mt-3 text-3xl font-medium tracking-tight">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="card-surface p-6">
        <div className="overline text-navy/40 mb-4">{t("os.workforce.hoursByProject")}</div>
        {(d.hours_by_project || []).length === 0 ? <p className="text-sm text-navy/35">{t("os.workforce.none")}</p> : d.hours_by_project.map((p) => (
          <div key={p.project} className="flex items-center justify-between py-1.5 border-b border-navy/5 last:border-0">
            <span className="text-sm text-navy/70">{p.project}</span><span className="badge badge-violet">{p.hours}h</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ ASISTENCIA ============ */
function AttendanceTab({ isLeader }) {
  const { t } = useI18n();
  const [today, setToday] = useState(null);
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (sc) => {
    setLoading(true);
    try {
      const [tRes, lRes] = await Promise.all([api.get("/workforce/attendance/today"), api.get("/workforce/attendance", { params: { scope: sc } })]);
      setToday(tRes.data); setItems(lRes.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(scope); }, [scope, load]);
  const clockIn = async () => { try { await api.post("/workforce/attendance/clock-in"); toast.success(t("os.workforce.clockIn")); load(scope); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const clockOut = async () => { try { const r = await api.post("/workforce/attendance/clock-out"); toast.success(`${r.data.hours}h`); load(scope); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-6" data-testid="wf-attendance">
      <div className="card-surface p-6 flex flex-wrap items-center justify-between gap-4" data-testid="wf-clock-widget">
        <div>
          <div className="overline text-navy/40">{t("os.workforce.hoursToday")}</div>
          <div className="text-3xl font-medium mt-1">{today?.record?.hours ?? 0}h</div>
          <div className="text-sm text-navy/50 mt-1">
            {today?.clocked_in ? `${t("os.workforce.clockedInSince")} ${hhmm(today.record.clock_in)}` : t("os.workforce.notClockedIn")}
          </div>
        </div>
        {today?.clocked_in
          ? <button onClick={clockOut} className="btn btn-primary" data-testid="wf-clock-out-btn"><LogOut className="w-4 h-4" /> {t("os.workforce.clockOut")}</button>
          : <button onClick={clockIn} className="btn btn-primary" data-testid="wf-clock-in-btn"><LogIn className="w-4 h-4" /> {t("os.workforce.clockIn")}</button>}
      </div>
      {isLeader && (
        <div className="flex gap-2">
          <button onClick={() => setScope("mine")} className={`btn text-sm ${scope === "mine" ? "btn-primary" : "btn-ghost"}`}>{t("os.workforce.scopeMine")}</button>
          <button onClick={() => setScope("all")} className={`btn text-sm ${scope === "all" ? "btn-primary" : "btn-ghost"}`} data-testid="wf-att-all">{t("os.workforce.scopeAll")}</button>
        </div>
      )}
      {loading ? <div className="py-10 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : items.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm">{t("os.workforce.noAttendance")}</div>
        : (
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm" data-testid="wf-attendance-table">
              <thead><tr className="text-left text-navy/40 border-b border-navy/10">
                {scope === "all" && <th className="p-3 font-medium">{t("os.workforce.employee")}</th>}
                <th className="p-3 font-medium">{t("os.workforce.date")}</th><th className="p-3 font-medium">{t("os.workforce.entry")}</th>
                <th className="p-3 font-medium">{t("os.workforce.exit")}</th><th className="p-3 font-medium">{t("os.workforce.hours")}</th><th className="p-3 font-medium">{t("os.workforce.status")}</th>
              </tr></thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className="border-b border-navy/5" data-testid={`wf-att-${a.id}`}>
                    {scope === "all" && <td className="p-3">{a.user_name}</td>}
                    <td className="p-3">{a.date}</td><td className="p-3">{hhmm(a.clock_in)}</td>
                    <td className="p-3">{a.clock_out ? hhmm(a.clock_out) : "—"}</td><td className="p-3">{a.hours}h</td>
                    <td className="p-3"><span className={`badge ${a.status === "abierto" ? "badge-amber" : "badge-emerald"}`}>{t(`os.workforce.${a.status === "abierto" ? "open" : "closed"}`)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

/* ============ TURNOS ============ */
function ShiftsTab({ meta, isLeader }) {
  const { t } = useI18n();
  const [scope, setScope] = useState(isLeader ? "all" : "mine");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ user_id: "", date: "", start: "09:00", end: "18:00", label: "", area: "" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async (sc) => {
    setLoading(true);
    try { const r = await api.get("/workforce/shifts", { params: { scope: sc } }); setItems(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(scope); }, [scope, load]);
  const submit = async () => {
    if (!f.user_id || !f.date) return toast.error(t("os.workforce.date"));
    setSaving(true);
    try { await api.post("/workforce/shifts", f); toast.success(t("os.workforce.shiftCreated")); setOpen(false); setF({ user_id: "", date: "", start: "09:00", end: "18:00", label: "", area: "" }); load(scope); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/workforce/shifts/${id}`); load(scope); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="wf-shifts">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {isLeader ? (
          <div className="flex gap-2">
            <button onClick={() => setScope("all")} className={`btn text-sm ${scope === "all" ? "btn-primary" : "btn-ghost"}`}>{t("os.workforce.scopeAll")}</button>
            <button onClick={() => setScope("mine")} className={`btn text-sm ${scope === "mine" ? "btn-primary" : "btn-ghost"}`}>{t("os.workforce.scopeMine")}</button>
          </div>
        ) : <div className="text-sm text-navy/50">{t("os.workforce.scopeMine")}</div>}
        {isLeader && <button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="wf-new-shift-btn"><Plus className="w-4 h-4" /> {t("os.workforce.newShift")}</button>}
      </div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : items.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.workforce.noShifts")}</div>
        : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="wf-shifts-list">
            {items.map((s) => (
              <div key={s.id} className="card-surface p-4" data-testid={`wf-shift-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-medium">{s.date}</div><div className="text-sm text-navy/60">{s.start} – {s.end}</div></div>
                  {isLeader && <button onClick={() => del(s.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                </div>
                <div className="mt-2 text-xs text-navy/50">{scope === "all" ? s.user_name : ""}{s.label ? ` · ${s.label}` : ""}{s.area ? ` · ${s.area}` : ""}</div>
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.workforce.newShift")} onClose={() => setOpen(false)} testid="wf-shift-dialog">
          <div className="space-y-3">
            <EmployeePicker testid="wf-shift-user" value={f.user_id || null} scope="managed"
              placeholder={t("os.workforce.employee")} onChange={(emp) => setF({ ...f, user_id: emp ? emp.id : "" })} />
            <DatePicker testid="wf-shift-date" value={f.date} onChange={(v) => setF({ ...f, date: v })} />
            <div className="grid grid-cols-2 gap-3">
              <input type="time" className="field" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
              <input type="time" className="field" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
            </div>
            <input className="field" placeholder={t("os.workforce.label")} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
            <input className="field" placeholder={t("os.workforce.area")} value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.workforce.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="wf-shift-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.workforce.create")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ PARTES DE HORAS ============ */
function TimesheetsTab({ meta, isLeader }) {
  const { t } = useI18n();
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ date: "", project_id: "", task: "", hours: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async (sc) => {
    setLoading(true);
    try { const r = await api.get("/workforce/timesheets", { params: { scope: sc } }); setItems(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(scope); }, [scope, load]);
  const submit = async () => {
    if (!f.date || !f.hours) return toast.error(t("os.workforce.hours"));
    const proj = (meta.projects || []).find((p) => p.id === f.project_id);
    setSaving(true);
    try {
      await api.post("/workforce/timesheets", { date: f.date, project_id: f.project_id || null, project_name: proj?.name || "", project_source: proj?.source || null, task: f.task, hours: Number(f.hours), notes: f.notes });
      toast.success(t("os.workforce.timesheetLogged")); setOpen(false); setF({ date: "", project_id: "", task: "", hours: "", notes: "" }); load(scope);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const review = async (id, status) => { try { await api.patch(`/workforce/timesheets/${id}`, { status }); load(scope); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/workforce/timesheets/${id}`); load(scope); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const badge = (s) => s === "aprobado" ? "badge-emerald" : s === "rechazado" ? "badge-danger" : "badge-amber";
  return (
    <div className="space-y-5" data-testid="wf-timesheets">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button onClick={() => setScope("mine")} className={`btn text-sm ${scope === "mine" ? "btn-primary" : "btn-ghost"}`}>{t("os.workforce.scopeMine")}</button>
          {isLeader && <button onClick={() => setScope("all")} className={`btn text-sm ${scope === "all" ? "btn-primary" : "btn-ghost"}`} data-testid="wf-ts-all">{t("os.workforce.scopeAll")}</button>}
        </div>
        <button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="wf-new-timesheet-btn"><Plus className="w-4 h-4" /> {t("os.workforce.newTimesheet")}</button>
      </div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : items.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.workforce.noTimesheets")}</div>
        : (
          <div className="space-y-2" data-testid="wf-timesheets-list">
            {items.map((ts) => (
              <div key={ts.id} className="card-surface p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`wf-timesheet-${ts.id}`}>
                <div><div className="font-medium">{ts.project_name || t("os.workforce.none")} · {ts.hours}h</div><div className="text-xs text-navy/50">{ts.date}{ts.task ? ` · ${ts.task}` : ""}{scope === "all" ? ` · ${ts.user_name}` : ""}</div></div>
                <div className="flex items-center gap-2">
                  <span className={`badge ${badge(ts.status)}`}>{t(`os.workforce.tsStatus.${ts.status}`)}</span>
                  {isLeader && scope === "all" && ts.status === "pendiente" && (
                    <>
                      <button onClick={() => review(ts.id, "aprobado")} className="btn btn-ghost text-emerald-600 px-2" data-testid={`wf-approve-${ts.id}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => review(ts.id, "rechazado")} className="btn btn-ghost text-destructive px-2" data-testid={`wf-reject-${ts.id}`}><XCircle className="w-4 h-4" /></button>
                    </>
                  )}
                  {(ts.user_id === undefined || scope === "mine" || isLeader) && <button onClick={() => del(ts.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.workforce.newTimesheet")} onClose={() => setOpen(false)} testid="wf-timesheet-dialog">
          <div className="space-y-3">
            <DatePicker testid="wf-ts-date" value={f.date} onChange={(v) => setF({ ...f, date: v })} />
            <SelectField testid="wf-ts-project" className="field" value={f.project_id} placeholder={t("os.workforce.selectProject")} onChange={(v) => setF({ ...f, project_id: v })} options={(meta.projects || []).map((p) => ({ value: p.id, label: `${p.name} (${p.source})` }))} />
            <input className="field" placeholder={t("os.workforce.task")} value={f.task} onChange={(e) => setF({ ...f, task: e.target.value })} data-testid="wf-ts-task" />
            <input type="number" step="0.25" className="field" placeholder={t("os.workforce.hours")} value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="wf-ts-hours" />
            <textarea className="field" rows={2} placeholder={t("os.workforce.notes")} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.workforce.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="wf-ts-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.workforce.create")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ STUDIO ============ */
function PolicyTab() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/workforce/policy").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const save = async () => {
    setSaving(true);
    const numKeys = ["jornada_diaria", "factor_extra", "factor_festivo", "redondeo", "dia_pagado_horas", "descuento_retardo_monto"];
    const intKeys = ["tolerancia_retardo_min", "incapacidad_pagada_pct"];
    try {
      const festivos = (typeof p.festivos === "string" ? p.festivos.split(",").map((s) => s.trim()).filter(Boolean) : p.festivos);
      const body = { ...p, festivos };
      numKeys.forEach((k) => { body[k] = parseFloat(body[k]) || 0; });
      intKeys.forEach((k) => { body[k] = parseInt(body[k], 10) || 0; });
      const r = await api.put("/workforce/policy", body); setP(r.data); toast.success("Política guardada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const num = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const festStr = Array.isArray(p.festivos) ? p.festivos.join(", ") : (p.festivos || "");
  return (
    <div className="space-y-5 max-w-2xl" data-testid="wf-policy">
      <p className="text-sm text-navy/50">Reglas que alimentan la <b>nómina por horas</b>: jornada, tolerancia de retardos, festivos, factores de pago e incapacidad.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        <label className="text-xs text-navy/60">Jornada diaria (h)<input type="number" className="field mt-1" value={p.jornada_diaria} onChange={num("jornada_diaria")} data-testid="wf-pol-jornada" /></label>
        <label className="text-xs text-navy/60">Hora de entrada<input type="time" className="field mt-1" value={p.hora_entrada} onChange={num("hora_entrada")} data-testid="wf-pol-entrada" /></label>
        <label className="text-xs text-navy/60">Tolerancia retardo (min)<input type="number" className="field mt-1" value={p.tolerancia_retardo_min} onChange={num("tolerancia_retardo_min")} data-testid="wf-pol-tolerancia" /></label>
        <label className="text-xs text-navy/60">Factor horas extra<input type="number" step={0.5} className="field mt-1" value={p.factor_extra} onChange={num("factor_extra")} /></label>
        <label className="text-xs text-navy/60">Factor día festivo<input type="number" step={0.5} className="field mt-1" value={p.factor_festivo} onChange={num("factor_festivo")} data-testid="wf-pol-festivo-factor" /></label>
        <label className="text-xs text-navy/60">Redondeo horas<input type="number" step={0.05} className="field mt-1" value={p.redondeo} onChange={num("redondeo")} /></label>
        <label className="text-xs text-navy/60">Hrs/día pagado<input type="number" className="field mt-1" value={p.dia_pagado_horas} onChange={num("dia_pagado_horas")} /></label>
        <label className="text-xs text-navy/60">Incapacidad pagada (%)<input type="number" className="field mt-1" value={p.incapacidad_pagada_pct} onChange={num("incapacidad_pagada_pct")} data-testid="wf-pol-incap" /></label>
        <label className="text-xs text-navy/60">Descuento por retardo ($)<input type="number" className="field mt-1" value={p.descuento_retardo_monto} onChange={num("descuento_retardo_monto")} /></label>
      </div>
      <label className="text-xs text-navy/60 block">Días festivos (fechas YYYY-MM-DD separadas por coma)
        <input className="field mt-1" value={festStr} onChange={(e) => setP({ ...p, festivos: e.target.value })} placeholder="2026-05-01, 2026-09-16" data-testid="wf-pol-festivos" /></label>
      <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="wf-pol-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar política"}</button>
    </div>
  );
}

export default function WorkforceStudio() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isLeader = LEADERSHIP.includes(user?.role);
  const [meta, setMeta] = useState(null);
  const params = new URLSearchParams(window.location.search);
  const initialTab = params.get("tab");
  const validTabs = ["dashboard", "attendance", "shifts", "timesheets", "policy"];
  const [tab, setTab] = useState(validTabs.includes(initialTab) ? initialTab : (isLeader ? "dashboard" : "attendance"));
  useEffect(() => { api.get("/workforce/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const tabs = [
    ...(isLeader ? [{ key: "dashboard", label: t("os.workforce.tabs.dashboard"), icon: LayoutDashboard }] : []),
    { key: "attendance", label: t("os.workforce.tabs.attendance"), icon: Clock },
    { key: "shifts", label: t("os.workforce.tabs.shifts"), icon: CalendarRange },
    { key: "timesheets", label: t("os.workforce.tabs.timesheets"), icon: Timer },
    ...(isLeader ? [{ key: "policy", label: "Política", icon: Clock }] : []),
  ];

  return (
    <OSLayout title={t("os.workforce.title")}>
      <div className="space-y-8" data-testid="workforce-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> {t("os.workforce.overline")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.workforce.title")}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.workforce.subtitle")}</p>
        </motion.div>

        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-1">
          {tabs.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`wf-tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition-colors ${tab === tb.key ? "bg-gradient-violet text-white" : "text-navy/55 hover:text-navy hover:bg-navy/5"}`}>
              <tb.icon className="w-3.5 h-3.5" /> {tb.label}
            </button>
          ))}
        </div>

        {!meta ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "dashboard" && isLeader && <DashboardTab />}
            {tab === "attendance" && <AttendanceTab isLeader={isLeader} />}
            {tab === "shifts" && <ShiftsTab meta={meta} isLeader={isLeader} />}
            {tab === "timesheets" && <TimesheetsTab meta={meta} isLeader={isLeader} />}
            {tab === "policy" && isLeader && <PolicyTab />}
          </>
        )}
      </div>
    </OSLayout>
  );
}
